import { useState, useEffect, useCallback } from 'react';
import { ordersService } from '../lib/database';
import { getDataSource } from '../lib/config';
import { initialOrders } from '../data/orders';
import type { Order } from '../types';

interface UseOrdersReturn {
  orders: Order[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  create: (order: Omit<Order, 'id'>) => Promise<Order>;
  update: (id: string, updates: Partial<Order> | ((currentOrder: Order) => Partial<Order>)) => Promise<Order>;
  remove: (id: string) => Promise<void>;
  getById: (id: string) => Order | undefined;
}

export const useOrders = (enabled = true): UseOrdersReturn => {
  // Mock seed orders (ORD-1..ORD-4) are for the mock data source only. Never seed them for
  // Firebase: the subscription merge keeps unknown ids as "optimistic" orders, so the fake
  // unpaid orders would persist forever and inflate Pending Order Revenue in Reports.
  const [orders, setOrders] = useState<Order[]>(() => (getDataSource() === 'mock' ? initialOrders : []));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchOrders = useCallback(async () => {
    const dataSource = getDataSource();
    
    if (dataSource === 'mock') {
      setOrders(initialOrders);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const data = await ordersService.getRecent();
      // Safety: filter out any soft-deleted orders before putting into state
      setOrders(data.filter(o => !o.isDeleted));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load orders';
      setError(errorMessage);
      console.error('Error fetching orders:', err);
      // Keep whatever we already have; never fall back to mock seed orders on Firebase.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;

    const dataSource = getDataSource();
    if (dataSource === 'mock') {
      void fetchOrders();
      return;
    }

    let cleanupFn: (() => void) | null = null;

    if (dataSource === 'firebase') {
      setLoading(true);
      setError(null);
      let receivedFirstSnapshot = false;

      const mergeOrders = (prev: Order[], incoming: Order[]): Order[] => {
        const orderMap = new Map<string, Order>();
        for (const order of prev) {
          if (!order.isDeleted) orderMap.set(order.id, order);
        }
        for (const order of incoming) {
          if (!order.isDeleted) orderMap.set(order.id, order);
        }
        return Array.from(orderMap.values());
      };

      // Seed from server so POS/KDS have orders even if listeners race on empty cache.
      void ordersService.getRecent().then((data) => {
        setOrders((prev) => mergeOrders(prev, data));
        setLoading(false);
        receivedFirstSnapshot = true;
      }).catch((err) => {
        console.error('Error seeding orders:', err);
      });

      const safetyTimer = setTimeout(() => {
        if (receivedFirstSnapshot) return;
        void fetchOrders().finally(() => {
          receivedFirstSnapshot = true;
        });
      }, 5000);

      const unsubscribe = ordersService.subscribe((updatedOrders) => {
        receivedFirstSnapshot = true;
        if (updatedOrders.length === 0) {
          setLoading(false);
          return;
        }
        // Use functional update to merge with optimistic updates rather than completely replacing
        // This prevents race conditions where subscription fires before update completes
        setOrders(prev => {
          const orderMap = new Map<string, Order>();
          
          // First, add all orders from real-time subscription (source of truth)
          updatedOrders.forEach(order => {
            orderMap.set(order.id, order);
          });
          
          // Then, check if any optimistic updates are newer (by timestamp or items count)
          // This handles the case where optimistic update has more items than subscription
          prev.forEach(order => {
            const subscriptionOrder = orderMap.get(order.id);
            if (subscriptionOrder) {
              // If optimistic order has more items, it might be newer - keep it temporarily
              // But trust the subscription for the final state
              if (order.items && subscriptionOrder.items) {
                // Trust subscription as source of truth when counts differ
              }
              // Always use subscription data as source of truth
              orderMap.set(order.id, subscriptionOrder);
            } else {
              // Order not in subscription - might be a new optimistic update, keep it
              orderMap.set(order.id, order);
            }
          });
          
          // Exclude soft-deleted orders from client state
          return Array.from(orderMap.values()).filter(o => !o.isDeleted);
        });
        setLoading(false);
      });

      if (unsubscribe) {
        cleanupFn = () => {
          clearTimeout(safetyTimer);
          unsubscribe();
        };
      } else {
        cleanupFn = () => clearTimeout(safetyTimer);
      }
    }
    // Note: Supabase support has been removed - using Firebase only

    return () => {
      if (cleanupFn) {
        cleanupFn();
      }
    };
  }, [fetchOrders, enabled]);

  const create = useCallback(async (order: Omit<Order, 'id'>): Promise<Order> => {
    const dataSource = getDataSource();
    
    if (dataSource === 'mock') {
      const newOrder: Order = {
        ...order,
        id: `ORD-${Date.now()}`,
      };
      setOrders(prev => [...prev, newOrder]);
      return newOrder;
    }

    setError(null);
    try {
      const newOrder = await ordersService.create(order);
      // Real-time subscription will update state, so no local setOrders to avoid duplicates
      return newOrder;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create order';
      setError(errorMessage);
      throw err;
    }
  }, []);

  const update = useCallback(async (id: string, updates: Partial<Order> | ((currentOrder: Order) => Partial<Order>)): Promise<Order> => {
    const dataSource = getDataSource();
    
    // Use functional setState to always get the latest order state
    let originalOrder: Order | undefined;
    let optimisticOrder: Order;
    let resolvedUpdates: Partial<Order>;
    
    setOrders(prev => {
      const existingOrder = prev.find(o => o.id === id);
      if (!existingOrder) {
        return prev;
      }
      originalOrder = existingOrder;

      if (typeof updates === 'function') {
        resolvedUpdates = updates(existingOrder);
      } else {
        resolvedUpdates = updates;
      }

      optimisticOrder = { ...existingOrder, ...resolvedUpdates } as Order;
      return prev.map(o => o.id === id ? optimisticOrder : o);
    });

    if (!originalOrder) {
      // KDS may show orders from an explicit today fetch before the live listener has them.
      if (dataSource !== 'mock') {
        setError(null);
        try {
          const remote = await ordersService.getById(id);
          if (!remote || remote.isDeleted) {
            return Promise.resolve({} as Order);
          }
          resolvedUpdates =
            typeof updates === 'function' ? updates(remote) : updates;
          if (!resolvedUpdates || Object.keys(resolvedUpdates).length === 0) {
            return remote;
          }
          const updated = await ordersService.update(id, resolvedUpdates);
          setOrders((prev) => {
            const map = new Map(prev.map((o) => [o.id, o]));
            map.set(id, updated);
            return Array.from(map.values());
          });
          return updated;
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'Failed to update order';
          setError(errorMessage);
          console.error(`❌ Failed to update order ${id} (remote fallback):`, err);
          throw err;
        }
      }
      return Promise.resolve({} as Order);
    }
    
    if (dataSource === 'mock') {
      return optimisticOrder!;
    }

    setError(null);
    
    // Update database - wait for it to complete to ensure data is saved
    // The real-time subscription will update the state, but we need to ensure the update completes
    try {
      await ordersService.update(id, resolvedUpdates!);
      
      // The real-time subscription should update the state, but we can also update optimistically
      // However, we should trust the subscription more than the direct response
      // So we'll let the subscription handle the state update
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update order';
      setError(errorMessage);
      console.error(`❌ Failed to update order ${id}:`, err);
      // Revert optimistic update on error
      setOrders(prev => prev.map(o => o.id === id ? originalOrder! : o));
      throw err; // Re-throw to let caller know it failed
    }
    
    // Return optimistic order immediately (don't wait for database)
    return optimisticOrder!;
  }, []);

  const remove = useCallback(async (id: string): Promise<void> => {
    const dataSource = getDataSource();
    
    if (dataSource === 'mock') {
      setOrders(prev => prev.filter(o => o.id !== id));
      return;
    }

    setError(null);
    try {
      await ordersService.delete(id);
      // Remove from local state so it disappears from current UI,
      // but document remains in Firestore as soft-deleted.
      setOrders(prev => prev.filter(o => o.id !== id));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete order';
      setError(errorMessage);
      throw err;
    }
  }, []);

  const getById = useCallback((id: string): Order | undefined => {
    return orders.find(o => o.id === id);
  }, [orders]);

  return {
    orders,
    loading,
    error,
    refresh: fetchOrders,
    create,
    update,
    remove,
    getById,
  };
};

